import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs";

/**
 * A voice-to-EMR recording that must outlive any single page.
 *
 * The recorder used to be mounted inside the EMR page, so navigating away tore
 * down the mic, socket and timer. This context lifts the *identity* of each
 * active session to the app root; a single `<GlobalVoiceRecorder>` reads them
 * and keeps recording no matter which route the doctor is on.
 *
 * Several recordings can be held at once — one per patient. Exactly one is the
 * `active` session (its recorder captures audio); the rest are *parked*, which
 * the recorder honours as an auto-pause (mic suspended, socket idle, transcript
 * kept). The doctor switches which one is active from the floating pill stack.
 * A session ends only when its EMR is submitted or it is discarded.
 */
export interface VoiceSessionInfo {
  emrId: string;
  userId: string;
  petId: string;
  patientName?: string;
  clinicId?: string | null;
  emrStatus?: string | null;
}

/** How many recordings can be held open at once (open mics/sockets). */
export const MAX_CONCURRENT_VOICE_SESSIONS = 3;

type AppliedHandler = (data: IEMRUpdatePayloadProps) => void;

interface VoiceSessionContextValue {
  /** The active (recording) session, or null when every session is paused. */
  session: VoiceSessionInfo | null;
  /** All held sessions, active and parked. */
  sessions: VoiceSessionInfo[];
  /** `emrId` of the session currently capturing audio, or null. */
  activeEmrId: string | null;
  /**
   * Per-session remount nonce. Bumped on every `startSession` for that emrId, so
   * the recorder (keyed by `emrId:nonce`) remounts and re-arms auto-start on an
   * explicit (re)start, while plain navigation keeps it recording.
   */
  nonces: Record<string, number>;
  /** Expanded review panel vs. the minimised floating pill (active session). */
  isOpen: boolean;
  /**
   * Starts (or re-arms) a session and makes it active — any previously-active
   * session is parked (auto-paused), never torn down, so no audio is lost.
   * Returns `false` — changing nothing — only when the concurrent-session cap is
   * reached and this is a brand-new emrId.
   */
  startSession: (info: VoiceSessionInfo, opts?: { open?: boolean }) => boolean;
  /** Removes the session for `emrId` (or the active one when omitted). */
  endSession: (emrId?: string) => void;
  /** Make an already-held session the active (recording) one. */
  setActiveSession: (emrId: string) => void;
  /** Park every session — nothing records — without removing any. */
  pauseActive: () => void;
  /** Is there a held session for this emrId? (active or parked). */
  hasSession: (emrId: string) => boolean;
  /** Is there a held session for this pet? (active or parked). */
  hasSessionForPet: (petId: string) => boolean;
  openPanel: () => void;
  minimize: () => void;
  /** Recorders stream their elapsed seconds here so the pill stack can show it. */
  reportDuration: (emrId: string, seconds: number) => void;
  /** Last reported elapsed seconds for a session (frozen while it is parked). */
  getDuration: (emrId: string) => number;
  /**
   * The EMR page registers a handler for its own emrId so that applying the
   * preview updates its live state when the doctor happens to be on that page.
   * Returns an unsubscribe function.
   */
  registerAppliedHandler: (emrId: string, handler: AppliedHandler) => () => void;
  /** Called by a recorder (for its emrId) after it applies preview data. */
  notifyApplied: (emrId: string, data: IEMRUpdatePayloadProps) => void;
}

const STORAGE_KEY = "vms_voice_sessions";
// Cross-tab channel used to detect when a session was inherited via a copied
// sessionStorage (new/duplicated tab) rather than genuinely owned by this tab.
const OWNER_CHANNEL = "vms_voice_session_owner";

interface StoredState {
  sessions: VoiceSessionInfo[];
  activeEmrId: string | null;
}

// Was this document loaded by reloading the SAME tab (vs. a fresh navigation)?
// A tab opened via a link/right-click → "open in new tab" COPIES the opener's
// sessionStorage, so restoring blindly would show the opener's recording (wrong
// pet) in the new tab. Only a real reload should restore.
const isReloadNavigation = (): boolean => {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav?.type) return nav.type === "reload" || nav.type === "back_forward";
    // Legacy fallback (deprecated but still present in some browsers).
    // 1 === TYPE_RELOAD, 2 === TYPE_BACK_FORWARD.
    const legacyType = (performance as any)?.navigation?.type;
    return legacyType === 1 || legacyType === 2;
  } catch {
    return false;
  }
};

// Persisted so a same-tab reload restores the floating widgets. The audio itself
// is already durable in IndexedDB (see VoiceEMRRecorder/audioQueue.ts), so a
// recording resumes rather than being lost. A new tab must NOT inherit it — see
// `isReloadNavigation` — otherwise a floating pill shows the wrong pet.
const readStored = (): StoredState => {
  try {
    if (!isReloadNavigation()) return { sessions: [], activeEmrId: null };
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { sessions: [], activeEmrId: null };
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    const sessions = Array.isArray(parsed.sessions) ? parsed.sessions : [];
    const activeEmrId =
      typeof parsed.activeEmrId === "string" && sessions.some((s) => s.emrId === parsed.activeEmrId)
        ? parsed.activeEmrId
        : sessions[0]?.emrId ?? null;
    return { sessions, activeEmrId };
  } catch {
    return { sessions: [], activeEmrId: null };
  }
};

const VoiceSessionContext = createContext<VoiceSessionContextValue | null>(null);

export const VoiceSessionProvider = ({ children }: { children: ReactNode }) => {
  const initial = useRef<StoredState>(readStored());
  const [sessions, setSessions] = useState<VoiceSessionInfo[]>(() => initial.current.sessions);
  const [activeEmrId, setActiveEmrId] = useState<string | null>(() => initial.current.activeEmrId);
  const [nonces, setNonces] = useState<Record<string, number>>({});
  const [isOpen, setIsOpen] = useState(false);
  const appliedHandlers = useRef<Map<string, AppliedHandler>>(new Map());

  // Durations tick every second on the active recorder; keeping them in a ref
  // (read by the pill stack, which re-renders when the session list/active
  // changes) avoids a provider-wide re-render every second.
  const durationsRef = useRef<Map<string, number>>(new Map());

  // Refs mirror state so the cross-tab channel handler (bound once) and the
  // synchronous guards inside `startSession` see the latest values.
  const sessionsRef = useRef<VoiceSessionInfo[]>(sessions);
  const activeEmrIdRef = useRef<string | null>(activeEmrId);
  useEffect(() => {
    sessionsRef.current = sessions;
    activeEmrIdRef.current = activeEmrId;
    try {
      if (sessions.length) {
        const payload: StoredState = { sessions, activeEmrId };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Private-mode / quota failures must not break recording.
    }
  }, [sessions, activeEmrId]);

  // Cross-tab ownership guard. A new/duplicated tab copies the opener's
  // sessionStorage, so it can boot holding another tab's recordings (wrong pet).
  // On boot we ask, per restored emrId, "does any live tab already own this?";
  // if a live owner answers, we're a copy and drop that session. `verifyingRef`
  // holds the emrIds we're still checking during that brief boot window.
  const verifyingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(OWNER_CHANNEL);

    channel.onmessage = (event) => {
      const msg = event.data as { type?: string; emrId?: string } | undefined;
      if (!msg?.emrId) return;
      const iHold = sessionsRef.current.some((s) => s.emrId === msg.emrId);

      if (msg.type === "who-owns" && iHold && !verifyingRef.current.has(msg.emrId)) {
        // We hold this session and aren't verifying it → we're the live owner.
        channel.postMessage({ type: "i-own", emrId: msg.emrId });
      } else if (msg.type === "i-own" && verifyingRef.current.has(msg.emrId)) {
        // Another live tab owns a session we booted with → we're an inherited
        // copy of it. Drop just that session (also clears the copied storage).
        verifyingRef.current.delete(msg.emrId);
        setSessions((prev) => prev.filter((s) => s.emrId !== msg.emrId));
        setActiveEmrId((prev) => (prev === msg.emrId ? null : prev));
      }
    };

    const restored = sessionsRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (restored.length) {
      restored.forEach((s) => {
        verifyingRef.current.add(s.emrId);
        channel.postMessage({ type: "who-owns", emrId: s.emrId });
      });
      // No claim within the grace window → no other live owner (e.g. a genuine
      // same-tab reload), so we keep those sessions.
      timer = setTimeout(() => {
        verifyingRef.current.clear();
      }, 1200);
    }

    return () => {
      if (timer) clearTimeout(timer);
      channel.close();
    };
    // Runs once on mount; it reacts to refs, not React state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = useCallback(
    (info: VoiceSessionInfo, opts?: { open?: boolean }): boolean => {
      const exists = sessionsRef.current.some((s) => s.emrId === info.emrId);
      // Cap only bites for a brand-new recording; re-arming an existing one is
      // always fine (parking the previous active one loses nothing).
      if (!exists && sessionsRef.current.length >= MAX_CONCURRENT_VOICE_SESSIONS) {
        return false;
      }
      // Keep refs authoritative synchronously so back-to-back starts (and the
      // guard above) see the new state before the `[sessions]` effect runs.
      sessionsRef.current = exists
        ? sessionsRef.current.map((s) => (s.emrId === info.emrId ? info : s))
        : [...sessionsRef.current, info];
      activeEmrIdRef.current = info.emrId;
      setSessions(sessionsRef.current);
      setActiveEmrId(info.emrId);
      // Re-arm the recorder even when the same draft is reopened (second visit).
      setNonces((prev) => ({ ...prev, [info.emrId]: (prev[info.emrId] ?? 0) + 1 }));
      setIsOpen(opts?.open ?? false);
      return true;
    },
    [],
  );

  const endSession = useCallback((emrId?: string) => {
    const target = emrId ?? activeEmrIdRef.current;
    if (!target) return;
    sessionsRef.current = sessionsRef.current.filter((s) => s.emrId !== target);
    durationsRef.current.delete(target);
    setSessions(sessionsRef.current);
    // If the active session left, park everything rather than surprise-resuming
    // another patient — the doctor taps a pill to pick the next one to record.
    if (activeEmrIdRef.current === target) {
      activeEmrIdRef.current = null;
      setActiveEmrId(null);
    }
    setIsOpen(false);
  }, []);

  const setActiveSession = useCallback((emrId: string) => {
    if (!sessionsRef.current.some((s) => s.emrId === emrId)) return;
    activeEmrIdRef.current = emrId;
    setActiveEmrId(emrId);
  }, []);

  const pauseActive = useCallback(() => {
    activeEmrIdRef.current = null;
    setActiveEmrId(null);
    setIsOpen(false);
  }, []);

  const hasSession = useCallback((emrId: string) => sessionsRef.current.some((s) => s.emrId === emrId), []);
  const hasSessionForPet = useCallback(
    (petId: string) => sessionsRef.current.some((s) => s.petId === petId),
    [],
  );

  const openPanel = useCallback(() => setIsOpen(true), []);
  const minimize = useCallback(() => setIsOpen(false), []);

  const reportDuration = useCallback((emrId: string, seconds: number) => {
    durationsRef.current.set(emrId, seconds);
  }, []);
  const getDuration = useCallback((emrId: string) => durationsRef.current.get(emrId) ?? 0, []);

  const registerAppliedHandler = useCallback((emrId: string, handler: AppliedHandler) => {
    appliedHandlers.current.set(emrId, handler);
    return () => {
      if (appliedHandlers.current.get(emrId) === handler) {
        appliedHandlers.current.delete(emrId);
      }
    };
  }, []);

  const notifyApplied = useCallback((emrId: string, data: IEMRUpdatePayloadProps) => {
    if (!emrId) return;
    appliedHandlers.current.get(emrId)?.(data);
  }, []);

  const session = useMemo(
    () => sessions.find((s) => s.emrId === activeEmrId) ?? null,
    [sessions, activeEmrId],
  );

  const value = useMemo<VoiceSessionContextValue>(
    () => ({
      session,
      sessions,
      activeEmrId,
      nonces,
      isOpen,
      startSession,
      endSession,
      setActiveSession,
      pauseActive,
      hasSession,
      hasSessionForPet,
      openPanel,
      minimize,
      reportDuration,
      getDuration,
      registerAppliedHandler,
      notifyApplied,
    }),
    [
      session,
      sessions,
      activeEmrId,
      nonces,
      isOpen,
      startSession,
      endSession,
      setActiveSession,
      pauseActive,
      hasSession,
      hasSessionForPet,
      openPanel,
      minimize,
      reportDuration,
      getDuration,
      registerAppliedHandler,
      notifyApplied,
    ],
  );

  return <VoiceSessionContext.Provider value={value}>{children}</VoiceSessionContext.Provider>;
};

export const useVoiceSession = (): VoiceSessionContextValue => {
  const ctx = useContext(VoiceSessionContext);
  if (!ctx) throw new Error("useVoiceSession must be used within a VoiceSessionProvider");
  return ctx;
};

/** Non-throwing variant for components that may render outside the provider. */
export const useVoiceSessionOptional = (): VoiceSessionContextValue | null =>
  useContext(VoiceSessionContext);
